import BigNumber from 'bignumber.js'

import { defaultWorldchainConfig } from './configs'

import { normalize } from './normalize'

import { ChainId, NativeCurrency, Token } from '@uniswap/sdk-core'
import { nativeOnChain } from 'constants/tokens'
import { GetQuoteArgs } from '../state/routing/types'

import { getFeeDirect, getFeeWithAmountIn, getFeeWithAmountOut, isNativeToken } from './fee'
import { ZeroXRequestParams, ZeroXResponse } from './zero-x.types'

type IEstimate = GetQuoteArgs & {
  fee: string
  slippage: string
}

export class ZeroX {
  private config = defaultWorldchainConfig

  constructor(config = defaultWorldchainConfig) {
    this.config = Object.assign(this.config, config ?? {})
  }

  name(): string {
    return '0x'
  }

  enabled(chainId: number): boolean {
    return chainId === 480
  }

  private async zeroXRequest(data: ZeroXRequestParams): Promise<ZeroXResponse> {
    // Build query parameters string
    const url = `https://bridge.holdstation.com/0x/swap/allowance-holder/quote`

    const queryParams = new URLSearchParams()

    // Add all parameters from the data object
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString())
      }
    })

    // Make the fetch request
    const response = await fetch(`${url}?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'X-Bridge-Purpose': 'worldchain-app',
        'Content-Type': 'application/json',
      },
    })

    if (response.status < 200 || response.status >= 400) {
      throw new Error(`${response.status}`)
    }

    const result = await response.json()

    // Parse and return the JSON response
    return result as ZeroXResponse
  }

  async tokenRateUsd(token: { address: string; decimals: number }): Promise<number> {
    if (normalize(token.address) === normalize(this.config.stableCoins[0] || '')) {
      return 1
    }

    // Build query parameters string
    const data = await this.zeroXRequest({
      chainId: 480,
      sellToken: token.address,
      buyToken: this.config.stableCoins[0],
      sellAmount: new BigNumber(1).multipliedBy(10 ** token.decimals).toFixed(0),
      taker: this.config.spender,
      slippageBps: 0,
    })
    // Parse and return the JSON response
    return new BigNumber(data.buyAmount).dividedBy(10 ** 6).toNumber() // usdc decimals
  }

  private resolveToken(tokenAddress: string, decimals: number, symbol?: string): NativeCurrency | Token {
    if (isNativeToken(tokenAddress)) {
      return nativeOnChain(ChainId.WORLD_CHAIN)
    }

    const addr = normalize(tokenAddress)
    const { weth, wld } = this.config.tokens
    if (addr === normalize(weth.address)) {
      return new Token(ChainId.WORLD_CHAIN, weth.address, weth.decimals, weth.symbol, weth.name)
    }
    if (addr === normalize(wld.address)) {
      return new Token(ChainId.WORLD_CHAIN, wld.address, wld.decimals, wld.symbol, wld.name)
    }

    return new Token(ChainId.WORLD_CHAIN, tokenAddress, decimals, symbol, 'Token')
  }

  async estimate(params: IEstimate) {
    const { fee: rawFee = '0.2', slippage: rawSlippage = '10' } = params

    const amount = new BigNumber(params.amount)

    const tokenIn = this.resolveToken(params.tokenInAddress, params.tokenInDecimals, params.tokenInSymbol)
    const tokenOut = this.resolveToken(params.tokenOutAddress, params.tokenOutDecimals, params.tokenOutSymbol)

    // Convert the input amount to Wei
    let amountInWei = amount

    const fee = parseFloat(rawFee)
    if (isNaN(fee)) {
      throw new Error(`Invalid fee value: ${rawFee}`)
    }

    //Adjust the input amount if a fee is applicable
    if (
      getFeeDirect(
        tokenIn.wrapped.address,
        tokenOut.wrapped.address,
        this.config.stableCoins,
        this.config.tokens.wld.address
      ) === 0
    ) {
      amountInWei = amountInWei.minus(getFeeWithAmountIn(tokenOut.wrapped.address, amountInWei, fee)?.feeAmount ?? 0)
    }

    const slippage = parseFloat(rawSlippage)
    // Validate slippage to ensure it is within a safe range (0% to 100%)
    if (isNaN(slippage) || slippage < 0 || slippage > 100) {
      throw new Error('Invalid slippage value. It must be between 0 and 100.')
    }

    // Fetch the best rate for the swapFetch the best rate for the swap
    const data = await this.zeroXRequest({
      chainId: 480,
      sellToken: tokenIn.wrapped.address,
      buyToken: tokenOut.wrapped.address,
      sellAmount: new BigNumber(amountInWei).toFixed(0),
      taker: this.config.spender,
      slippageBps: slippage * 100, // 0 - > 1000 : 0% - > 10%
      tradeSurplusRecipient: this.config.tradeSurplusRecipient,
    })

    const amountOut = new BigNumber(data.buyAmount)

    let feeOut = new BigNumber(0)
    if (
      getFeeDirect(
        tokenIn.wrapped.address,
        tokenOut.wrapped.address,
        this.config.stableCoins,
        this.config.tokens.wld.address
      ) === 1
    ) {
      feeOut = new BigNumber(getFeeWithAmountOut(`0x${amountOut.toString(16)}`, fee)?.feeAmount ?? '0')
    }

    // Calculate the minimum output amount after slippage and fees
    const amountOutMin = amountOut
      .multipliedBy(new BigNumber(1).minus(Number(slippage) / 10000))
      .integerValue(BigNumber.ROUND_DOWN)
      .minus(feeOut)

    // rateSwap = amountIn / (amountOut / 10^decimals)
    const rateSwap = amount //
      .dividedBy(amountOut)
      .multipliedBy(10 ** tokenOut.decimals)

    const rateTokenIn = await this.tokenRateUsd({
      address: tokenIn.wrapped.address,
      decimals: tokenIn.decimals,
    })

    const rateTokenOut = await this.tokenRateUsd({
      address: tokenOut.wrapped.address,
      decimals: tokenOut.decimals,
    })

    const minmumReceived = amountOutMin.div(Math.pow(10, tokenOut.decimals))

    const amountInUsd = new BigNumber(amount).div(10 ** tokenIn.decimals).multipliedBy(rateTokenIn)
    // amountUsd = tokenAmount / 10^decimals * rateUsdPerToken
    const amountOutUsd = new BigNumber(amountOut).div(10 ** tokenOut.decimals).multipliedBy(rateTokenOut)

    // Return the response with calculated values and populated transaction
    const resp = {
      data: data.transaction.data ?? '',
      value: isNativeToken(tokenIn.wrapped.address) ? amountInWei.toFixed() : '0',
      to: data.transaction.to ?? '',
      addons: {
        sellAmount: amount.toNumber(),
        buyAmount: amountOut.toNumber(),
        sellOutUsd: amountInUsd.toNumber(),
        buyOutUsd: amountOutUsd.toNumber(),
        sellRawAmount: amount.div(10 ** tokenIn.decimals).toFixed(),
        buyRawAmount: amountOut.div(10 ** tokenOut.decimals).toFixed(),
        rateSwap: rateSwap.toString(),
        rateTokenIn,
        rateTokenOut,
        totalFee: data.totalNetworkFee,
        minReceived: minmumReceived.toString(),
        feeAmountOut: `0x${feeOut.toString(16)}`,
        feeOut: feeOut.toString(),
        tokenIn: tokenIn.wrapped.address,
        tokenOut: tokenOut.wrapped.address,
      },
    }
    return resp
  }
}

export const XModume = new ZeroX()
